import { HelpSection } from '../models/help-section.model';
import { parseHelpDocument, serializeHelpDocument } from './help-document.util';

/** Clases típicas de Word / PDF al pegar */
const WORD_CLASS_PATTERN = /^(SCXW\d+|BCX\d+|WACImage|NormalTextRun|TextRun|OutlineElement|Paragraph|Ltr|Rtl)$/i;

/** Atributos que no aportan al visor de ayudas */
const REMOVED_ATTRIBUTES = new Set([
  'style',
  'xml:lang',
  'lang',
  'data-contrast',
  'unselectable',
  'uploadprocessed',
  'contenteditable',
  'role',
  'aria-level',
  'aria-posinset',
]);

/** Clases que conviene conservar (CKEditor / tablas / imágenes) */
const KEPT_CLASSES = new Set(['table', 'image', 'image_resized', 'ck-table-resized', 'ck-widget', 'ck-editor__editable']);

function shouldKeepClass(className: string): boolean {
  const token = className.trim();
  if (!token) return false;
  if (KEPT_CLASSES.has(token)) return true;
  if (token.startsWith('ck-')) return true;
  if (token.startsWith('image-style-')) return true;
  if (token === 'image-inline') return true;
  if (WORD_CLASS_PATTERN.test(token)) return false;
  if (/^SCXW/i.test(token)) return false;
  return false;
}

function filterClassAttribute(value: string): string | null {
  const kept = value
    .split(/\s+/)
    .map((c) => c.trim())
    .filter((c) => c && shouldKeepClass(c));
  return kept.length ? kept.join(' ') : null;
}

function preserveWidthStyle(style: string): string | null {
  const width = style.match(/\bwidth\s*:\s*([^;]+)/i)?.[1]?.trim();
  return width ? `width:${width}` : null;
}

function preserveTextAlignStyle(style: string): string | null {
  const textAlign = style.match(/\btext-align\s*:\s*([^;]+)/i)?.[1]?.trim();
  return textAlign ? `text-align:${textAlign}` : null;
}

function combineStyles(...parts: Array<string | null | undefined>): string | null {
  const styles = parts.filter((part): part is string => !!part?.trim());
  return styles.length ? styles.join(';') : null;
}

function simplifyStyle(el: Element, style: string): string | null {
  const tag = el.tagName.toLowerCase();

  if (tag === 'col') {
    return preserveWidthStyle(style);
  }

  if (tag === 'td' || tag === 'th') {
    return combineStyles(preserveWidthStyle(style), preserveTextAlignStyle(style));
  }

  if (tag === 'p' || /^h[1-6]$/.test(tag)) {
    return preserveTextAlignStyle(style);
  }

  if (tag === 'figure') {
    const width = style.match(/\bwidth\s*:\s*([^;]+)/i)?.[1]?.trim();
    const textAlign = preserveTextAlignStyle(style);

    if (!width) {
      return textAlign;
    }

    const isTableFigure = el.classList.contains('table');
    if (isTableFigure) {
      const inCell = !!el.closest('td, th');
      if (inCell) return combineStyles('width:100%', textAlign);

      const pct = parseFloat(width);
      if (width.endsWith('%') && !Number.isNaN(pct)) {
        return combineStyles(pct > 100 ? 'width:100%' : `width:${width}`, textAlign);
      }
      return combineStyles('width:100%', textAlign);
    }

    return combineStyles(preserveWidthStyle(style), textAlign);
  }

  return null;
}

function unwrapElement(el: Element): void {
  const parent = el.parentElement;
  if (!parent) return;
  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el);
  }
  parent.removeChild(el);
}

function isRedundantWrapper(el: Element): boolean {
  const tag = el.tagName.toUpperCase();
  if (tag !== 'SPAN' && tag !== 'DIV') return false;

  const cls = el.getAttribute('class') ?? '';
  const hasWordClass = cls.split(/\s+/).some((c) => WORD_CLASS_PATTERN.test(c) || /^SCXW/i.test(c));
  if (hasWordClass) return true;

  if (tag === 'SPAN' && !el.querySelector('table, figure, img, ul, ol')) {
    return true;
  }

  if (tag === 'DIV' && hasWordClass && !el.querySelector('table, figure')) {
    return true;
  }

  if (
    tag === 'DIV' &&
    !el.getAttribute('class') &&
    !el.getAttribute('style') &&
    el.childElementCount === 1 &&
    el.textContent?.trim() === el.firstElementChild?.textContent?.trim()
  ) {
    const childTag = el.firstElementChild?.tagName;
    if (childTag && ['FIGURE', 'TABLE', 'P'].includes(childTag)) {
      return true;
    }
  }

  return false;
}

function cleanElementTree(el: Element, isSanitizeRoot = false): void {
  Array.from(el.children).forEach((child) => cleanElementTree(child));

  Array.from(el.attributes).forEach((attr) => {
    const name = attr.name.toLowerCase();

    if (name === 'class') {
      const filtered = filterClassAttribute(attr.value);
      if (filtered) el.setAttribute('class', filtered);
      else el.removeAttribute('class');
      return;
    }

    if (name === 'style') {
      const simplified = simplifyStyle(el, attr.value);
      if (simplified) el.setAttribute('style', simplified);
      else el.removeAttribute('style');
      return;
    }

    if (REMOVED_ATTRIBUTES.has(name)) {
      el.removeAttribute(attr.name);
    }
  });

  if (!isSanitizeRoot && isRedundantWrapper(el)) {
    unwrapElement(el);
  }
}

/** Ajusta tablas CKEditor tras limpiar atributos de Word */
function normalizeCkTables(root: Element): void {
  root.querySelectorAll('figure.table').forEach((fig) => {
    if (fig.closest('td, th')) {
      fig.setAttribute('style', 'width:100%');
    }
  });

  root.querySelectorAll('table.ck-table-resized').forEach((node) => {
    const table = node as HTMLTableElement;
    const cols = table.querySelectorAll(':scope > colgroup > col, colgroup > col');
    const hasColWidth = Array.from(cols).some((col) => col.getAttribute('style')?.includes('width') || col.hasAttribute('width'));
    if (hasColWidth || cols.length < 2) return;

    const firstRow = table.rows[0];
    const cellCount = firstRow?.cells.length ?? 0;
    if (cellCount !== cols.length) return;

    const cells = firstRow!.cells;

    if (cols.length === 2) {
      const firstCell = cells[0];
      const secondCell = cells[1];
      const firstImg = firstCell?.querySelector('img');
      const firstText = (firstCell?.textContent ?? '').replace(/\s/g, '');
      const secondHasTable = secondCell?.querySelector('figure.table, table');
      if (firstImg && !firstText && secondHasTable) {
        cols[0].setAttribute('style', 'width:15%');
        cols[1].setAttribute('style', 'width:85%');
        return;
      }
    }

    Array.from(cells).forEach((cell: HTMLTableCellElement, index) => {
      const col = cols[index];
      if (!col || col.getAttribute('style')?.includes('width')) return;
      const img = cell.querySelector('img');
      const text = (cell.textContent ?? '').trim();
      if (img && !text) {
        col.setAttribute('style', 'width:12%');
      }
    });
  });
}

/**
 * Limpia un fragmento HTML (contenido de sección o documento legacy).
 * Elimina ruido de Word/PDF conservando tablas, imágenes y estructura básica.
 */
export function sanitizeHelpHtmlFragment(html: string | null | undefined): string {
  const raw = (html ?? '').trim();
  if (!raw) return '';

  const doc = new DOMParser().parseFromString(`<div id="help-sanitize-root">${raw}</div>`, 'text/html');
  const root = doc.getElementById('help-sanitize-root');
  if (!root) return raw;

  cleanElementTree(root, true);
  normalizeCkTables(root);
  return root.innerHTML.trim();
}

/**
 * Limpia documento v1 (secciones) o HTML legacy completo.
 */
export function sanitizeHelpHtml(html: string | null | undefined): string {
  const raw = (html ?? '').trim();
  if (!raw) return '';

  const doc = parseHelpDocument(raw);
  if (doc.mode === 'sections') {
    const sections: HelpSection[] = doc.sections.map((s) => ({
      ...s,
      title: s.title,
      content: sanitizeHelpHtmlFragment(s.content),
    }));
    return serializeHelpDocument(sections);
  }

  return sanitizeHelpHtmlFragment(doc.legacyHtml ?? raw);
}

/**
 * Registra limpieza al pegar en CKEditor 5 (Word, PDF, web).
 */
export function attachHelpEditorPasteSanitizer(editor: { editing: { view: { document: { on: (...args: unknown[]) => void } } } }): void {
  editor.editing.view.document.on(
    'clipboardInput',
    (_evt: unknown, data: { dataTransfer?: { getData: (t: string) => string; setData: (t: string, v: string) => void } }) => {
      const transfer = data?.dataTransfer;
      if (!transfer) return;

      const html = transfer.getData('text/html');
      if (!html?.trim()) return;

      transfer.setData('text/html', sanitizeHelpHtmlFragment(html));
    },
    { priority: 'high' },
  );
}

/**
 * Sanitiza HTML obtenido del editor antes de persistir en el modelo.
 */
export function sanitizeEditorOutput(html: string | null | undefined): string {
  return sanitizeHelpHtmlFragment(html);
}
